/**
 * TarotCardPlaceholder.tsx
 *
 * Fallback component rendered inside a `DayCell` or `TarotCardPopup` when the
 * card's image URL is unavailable or fails to load.
 *
 * Displays a stylised placeholder that communicates the card's identity through: * - A gradient background derived from the card's suit or arcana.
 * - A decorative double-border frame.
 * - A suit/arcana symbol (emoji) centred in the card.
 * - The card's short name at the bottom.
 *
 * ## Colour mapping
 * Each suit and the Major Arcana have a distinct top/bottom gradient pair and
 * a representative symbol:
 *
 * | Key       | Top colour | Bottom colour | Symbol |
 * |-----------|-----------|---------------|--------|
 * | Wands     | Gold      | Burnt orange  | 🔥     |
 * | Cups      | Blue      | Deep navy     | 🌊     |
 * | Swords    | Silver    | Slate         | ⚔️     |
 * | Pentacles | Gold      | Forest green  | ⭐     |
 * | major     | Mauve     | Deep purple   | ✦      |
 *
 * @prop cardName - Full name of the card, used for the `aria-label` and the
 *                  truncated display name.
 * @prop suit     - Optional suit name (e.g. `"Wands"`). When absent, `arcana`
 *                  is used as the colour-map key.
 * @prop arcana   - Whether the card is `"major"` or `"minor"` arcana.
 */

'use client';

import React from 'react';

/** Props accepted by `TarotCardPlaceholder`. */
interface Props {
  /** Full card name, e.g. `"The High Priestess"` or `"Ace of Cups"`. */
  cardName: string;
  /** Optional suit name used to select the gradient colour scheme. */
  suit?: string;
  /** Arcana type; used as fallback colour-map key when `suit` is absent. */
  arcana: 'major' | 'minor';
}

/**
 * Colour and symbol definitions for each suit and the Major Arcana.
 * Keys match the values of `TarotCard.suit` (title-cased) or `"major"`.
 */
const SUIT_COLORS: Record<string, { top: string; bot: string; symbol: string }> = {
  Wands:     { top: '#c9a84c', bot: '#8b4a1e', symbol: '🔥' },
  Cups:      { top: '#4a8bc9', bot: '#1e3d8b', symbol: '🌊' },
  Swords:    { top: '#c9c9c9', bot: '#4a4a6b', symbol: '⚔️' },
  Pentacles: { top: '#c9a84c', bot: '#2d4a1e', symbol: '⭐' },
  major:     { top: '#8b4a6b', bot: '#2d1f3d', symbol: '✦' },
};

export default function TarotCardPlaceholder({ cardName, suit, arcana }: Props) {
  const key = suit ?? arcana;
  const colors = SUIT_COLORS[key] ?? SUIT_COLORS.major;

  // Extract short name for display
  const shortName = cardName.length > 14 ? cardName.split(' ').slice(-2).join(' ') : cardName;

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-between p-2 relative overflow-hidden"
      style={{ background: `linear-gradient(160deg, ${colors.top}22 0%, ${colors.bot}44 100%)` }}
      aria-label={`Placeholder image for ${cardName}`}
    >
      {/* Border frame */}
      <div
        className="absolute inset-1 rounded border opacity-30"
        style={{ borderColor: colors.top }}
      />
      <div
        className="absolute inset-2 rounded border opacity-15"
        style={{ borderColor: colors.top }}
      />

      {/* Top ornament */}
      <div className="relative z-10 mt-1">
        <span className="text-xs" style={{ color: colors.top, opacity: 0.7 }}>✦ ✦ ✦</span>
      </div>

      {/* Center symbol */}
      <div className="relative z-10 flex flex-col items-center gap-1 flex-1 justify-center">
        <span className="text-2xl sm:text-3xl" role="img" aria-hidden>{colors.symbol}</span>
        <div
          className="w-8 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${colors.top}, transparent)` }}
        />
      </div>

      {/* Card name */}
      <div className="relative z-10 mb-1 text-center px-1">
        <p
          className="font-display text-[8px] sm:text-[9px] leading-tight tracking-wider text-center"
          style={{ color: colors.top, opacity: 0.85 }}
        >
          {shortName}
        </p>
      </div>
    </div>
  );
}